import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { AlertCircle } from "lucide-react";
import { format } from "date-fns";
import { Link } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/lib/supabase";
import { InvoiceGroup, Client, formSchema, InvoiceFormValues, MissingBillingDetailsType } from "@/types/invoice";
import { checkClientBillingDetails, generateInvoiceNumber } from "@/utils/invoiceUtils";
import { useTranslation } from "@/i18n/useTranslation";
import { Language } from "@/i18n/translations";

interface NewInvoiceFormProps {
  activePeriod: string | null;
  groupedInvoices: InvoiceGroup[];
  clients: Client[];
  onCancel: () => void;
  onSuccess: () => void;
  language: Language;
}

const NewInvoiceForm = ({
  activePeriod,
  groupedInvoices,
  clients,
  onCancel,
  onSuccess,
  language
}: NewInvoiceFormProps) => {
  const { toast } = useToast();
  const [missingBillingDetails, setMissingBillingDetails] = useState<MissingBillingDetailsType>(null);
  const { t } = useTranslation();

  const form = useForm<InvoiceFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {}
  });

  const onSubmit = async (values: InvoiceFormValues) => {
    try {
      if (!activePeriod) {
        toast({
          title: "Error: No active period selected",
          variant: "destructive",
        });
        return;
      }
      
      const hasAllBillingDetails = await checkClientBillingDetails(
        values.client_id, 
        setMissingBillingDetails, 
        toast, 
        t('invoices.errorCheckingBillingDetails')
      );
      
      if (!hasAllBillingDetails) {
        return;
      }
      
      const selectedPeriod = groupedInvoices.find(group => group.periodKey === activePeriod);
      if (!selectedPeriod) {
        toast({
          title: "Error: Period not found",
          variant: "destructive",
        });
        return;
      }
      
      const { data: profile } = await supabase.auth.getUser();
      if (!profile.user) throw new Error('Not authenticated');

      const { data: latestClientData, error: clientError } = await supabase
        .from('clients')
        .select(`
          id, 
          name,
          short_name,
          legal_name,
          billing_details
        `)
        .eq('id', values.client_id)
        .single();
        
      if (clientError) throw clientError;
      
      const selectedClient = clients.find(c => c.id === values.client_id);
      
      const invoiceNumber = generateInvoiceNumber(
        selectedClient, 
        selectedPeriod.fromDate
      );

      const formattedData = {
        client_id: values.client_id,
        invoice_number: invoiceNumber,
        amount: 0,
        currency: 'USD',
        billing_period_start: format(selectedPeriod.fromDate, 'yyyy-MM-dd'),
        billing_period_end: format(selectedPeriod.toDate, 'yyyy-MM-dd'),
        status: 'draft' as const,
      };

      console.log("Submitting invoice with data:", formattedData);

      const { error } = await supabase
        .from('invoices')
        .insert(formattedData);

      if (error) throw error;

      toast({
        title: t('invoices.invoiceCreated'),
      });

      form.reset();
      onSuccess();
    } catch (error) {
      console.error('Error creating invoice:', error);
      toast({
        title: t('invoices.errorCreatingInvoice'),
        variant: "destructive",
      });
    }
  };

  return (
    <Card className="mb-8">
      <CardHeader>
        <CardTitle>{t('invoices.createInvoice')}</CardTitle>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="client_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('invoices.client')}</FormLabel>
                  <Select 
                    onValueChange={(value) => {
                      field.onChange(parseInt(value, 10));
                      setMissingBillingDetails(null);
                    }}
                    defaultValue={field.value?.toString()}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder={t('invoices.selectClient')} />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {clients.length > 0 ? (
                        clients.map((client) => (
                          <SelectItem key={client.id} value={client.id.toString()}>
                            {client.name}
                          </SelectItem>
                        ))
                      ) : (
                        <SelectItem value="loading" disabled>
                          {t('invoices.loading')}
                        </SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            {missingBillingDetails && (
              <Alert variant="destructive" className="mt-4">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>{language === 'en' ? 'Missing billing details' : 'Faltan detalles de facturación'}</AlertTitle>
                <AlertDescription>
                  <div className="mb-2">
                    {language === 'en' 
                      ? `Client "${missingBillingDetails.clientName}" is missing the following billing details:`
                      : `Al cliente "${missingBillingDetails.clientName}" le faltan los siguientes detalles de facturación:`
                    }
                  </div>
                  <ul className="list-disc pl-5 mb-3">
                    {missingBillingDetails.missingFields.map((field, index) => (
                      <li key={index}>{field}</li>
                    ))}
                  </ul>
                  <div>
                    <Link 
                      to={`/clients/${missingBillingDetails.clientId}`} 
                      className="text-primary underline font-medium"
                    >
                      {language === 'en' ? 'Update client information' : 'Actualizar información del cliente'}
                    </Link>
                  </div>
                </AlertDescription>
              </Alert>
            )}
            
            <div className="text-sm text-muted-foreground italic">
              {t('invoices.autoGeneratedInvoiceNumber')}
            </div>
            
            <div className="bg-muted p-3 rounded-md">
              <p className="text-sm font-medium">{t('invoices.dateRange')}:</p>
              <p className="text-sm">
                {groupedInvoices.find(g => g.periodKey === activePeriod)?.period || 'Unknown period'}
              </p>
            </div>
            
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={onCancel}>
                {t('invoices.cancel')}
              </Button>
              <Button type="submit">{t('invoices.save')}</Button>
            </div>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
};

export default NewInvoiceForm;
